/* __next_internal_client_entry_do_not_use__ default auto */ import { jsx as _jsx } from "react/jsx-runtime";
import { useSitecoreContext } from 'lib/hooks/useSitecoreContext';
import { useState, useCallback, useMemo, useRef, useEffect, isValidElement } from 'react';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import MultiStep from 'helpers/MultiStep/MultiStep';
import ConveyancingStep1 from 'helpers/ConveyancingSteps/ConveyancingStep1';
import ConveyancingStep2 from 'helpers/ConveyancingSteps/ConveyancingStep2';
import ConveyancingStep3 from 'helpers/ConveyancingSteps/ConveyancingStep3';
import { useSearchParams } from 'next/navigation';
import { useSessionStorage } from 'lib/hooks/useSessionStorage';
import ThankYou from 'helpers/ConveyancingSteps/ThankYou';
import { CONVEYANCING_CALCULATOR_CONSTANTS, FORM_CONSTANTS } from 'helpers/Constants/SiteConfig';
import { apiRequest } from 'lib/utils/apiWrapper';
import { sendGTMEvent } from '@next/third-parties/google';
import { toolStartFinishGTMEvent, toolSectionCompleteGTMEvent } from 'lib/utils/gtm-utils';
import { encryptData } from 'lib/utils/encryption';
const ConveyancingCalculatorInner = (props)=>{
    const fields = props.fields ?? {};
    const step1Ref = useRef(null);
    const step3Ref = useRef(null);
    // State to track if ThankYou component should be shown
    const [showThankYou, setShowThankYou] = useState(false);
    // State to store processed thankyouSuccessMessage with replaced tokens
    const [processedThankYouMessage, setProcessedThankYouMessage] = useState(null);
    const [submitStatus, setSubmitStatus] = useState('idle');
    const [isLoading, setIsLoading] = useState(false);
    const { executeRecaptcha } = useGoogleReCaptcha();
    // Use session storage hook
    const { saveFormDataToStorage, getFormDataFromStorage, clearAllFormDataFromStorage, getAllFormDataFromStorage } = useSessionStorage('conveyancing-form-data');
    const { step1FooterText, step1NextButtonText, step3Subtitle, step3Title, step2Subtitle, step2Title, step3FinishButtonText, step3FooterText, step1Subtitle, step1Title, step2FooterText, step2NextButtonText, stepBackButtonText, stepExitButtonText, step1FormGuidanceRemortgageTitle, step1FormRemortgage, step1FormGuidanceBuyingAndSellingTitle, step1FormGuidanceBuyingTitle, step1FormGuidanceRemortgageDescription, step1FormGuidanceSellingDescription, step1FormGuidanceSellingTitle, step1FormBuying, step1FormBuyingTitle, step1FormGuidanceBuyingAndSellingDescription, step1FormGuidanceBuyingDescription, step1FormSelling, step1FormSellingTitle, additionalFeesLabel, additionalFeesDescription, leaseholdFee, mandatoryFeeSearches, noSearchPolicyLabel, purchaseFeeTabLabel, step2FormGuidanceSellingTitle, step2PurchaseFeesTitle, vatFeePercent, vatLabel, landRegistryFee, landRegistryFeeBuyingTooltip, mandatoryFeesTitleBuying, mandatoryFeeTitleRemortgage, salesFeeTabLabel, step2FormGuidanceBuyingDescription, step2RemortgageFeesTitle, step2SellingFeesTitle, telegraphicTransferFee, telegraphicTransferFeeLabel, landRegistryFeeLabel, landRegistryFeeRemortgageTooltip, mandatoryFeeTitleSelling, mortgageFee, step2FormGuidanceRemortgageTitle, step2FormGuidanceSellingDescription, totalLabel, totalPurchaseMandatoryFeeAndTaxesLabel, totalRemortgageMandatoryFeeAndTaxesLabel, totalSellingMandatoryFeeAndTaxesLabel, additionalFees, legalFee, noSearchPolicyFee, step2FormGuidanceBuyingTitle, step2FormGuidanceRemortgageDescription, telegraphicTransferFeeTooltip, totalFeesInclVatLabel, vatTooltip, step3Form, step3FormGuidanceDescription, step3FormGuidanceTitle, landRegistryFeeBuyingTooltipTitle, landRegistryFeeRemortgageTooltipTitle, noSearchPolicyFeeTooltip, noSearchPolicyFeeTooltipTitle, telegraphicTransferFeeTooltipTitle, vatTooltipTitle, thankyouTitle, thankyouSuccessMessage, conveyancingCTALink, newjourneyCTALink, printDescription, printLogoIcon, printTitle, emailTemplate } = fields;
    const searchParams = useSearchParams();
    const selectedOption = searchParams?.get('selectedOption') || 'buy';
    const defaultSteps = [
        {
            stepLabel: step1Title,
            title: step1Subtitle
        },
        {
            stepLabel: step2Title,
            title: step2Subtitle
        },
        {
            stepLabel: step3Title,
            title: step3Subtitle
        }
    ];
    const getToolName = useCallback(()=>{
        switch(selectedOption){
            case 'buy':
                return 'conveyancingCalc_buy';
            case 'sell':
                return 'conveyancingCalc_sell';
            case 'remortgage':
                return 'conveyancingCalc_remortgage';
            case 'buyandsell':
                return 'conveyancingCalc_buySell';
            default:
                return 'conveyancingCalc_buy';
        }
    }, [
        selectedOption
    ]);
    useEffect(()=>{
        const gtmEvent = toolStartFinishGTMEvent({
            toolName: getToolName(),
            event: 'tool_start'
        });
        sendGTMEvent(gtmEvent);
    }, [
        getToolName
    ]);
    // Reset step and form data only when selectedOption changes (based on sessionStorage)
    useEffect(()=>{
        const currentOption = selectedOption && typeof selectedOption === 'string' ? selectedOption : 'buy';
        // Get previous selectedOption from sessionStorage
        let prevOption = null;
        if ("object" !== 'undefined') {
            try {
                prevOption = window.sessionStorage.getItem('conveyancing-selected-option');
            } catch (error) {
                console.error('Failed to get selectedOption from sessionStorage:', error);
            }
        }
        // Reset all sessionStorage data on every selectedOption change
        // Clear if: selectedOption changed OR first time selecting an option (prevOption is null)
        if (prevOption !== currentOption) {
            clearAllFormDataFromStorage();
            // Verify it's cleared by checking sessionStorage directly
            if ("object" !== 'undefined') {
                try {
                    const remaining = window.sessionStorage.getItem('conveyancing-form-data');
                    if (remaining !== null) {
                        // Force remove if still present
                        window.sessionStorage.removeItem('conveyancing-form-data');
                    }
                    // Clear step storage to reset MultiStep to step 0
                    // Remove the item first, then set to '0' to ensure MultiStep detects the change
                    window.sessionStorage.removeItem('conveyancing-steps');
                    window.sessionStorage.setItem('conveyancing-steps', '0');
                // Don't remove conveyancing-reset-key here - let MultiStep handle it via resetKey prop
                // MultiStep will update conveyancing-reset-key when resetKey changes
                } catch (error) {
                    console.error('Failed to clear step storage:', error);
                }
            }
            setStep1FormData(null);
            setStep1InitialValues(null);
            setStep3InitialValues(null);
            setProcessedThankYouMessage(null);
        }
        // Update sessionStorage with current selectedOption
        if ("object" !== 'undefined') {
            try {
                window.sessionStorage.setItem('conveyancing-selected-option', currentOption);
            } catch (error) {
                console.error('Failed to save selectedOption to sessionStorage:', error);
            }
        }
    }, [
        selectedOption,
        clearAllFormDataFromStorage,
        getAllFormDataFromStorage
    ]);
    // Store form data in state to trigger recalculation of step 2 content
    const [step1FormData, setStep1FormData] = useState(()=>getFormDataFromStorage(0));
    // Store initial values from sessionStorage - reload when navigating to steps with forms
    const [step1InitialValues, setStep1InitialValues] = useState(()=>getFormDataFromStorage(0));
    // Track real-time form values for dynamic filtering
    const [realTimeFormValues, setRealTimeFormValues] = useState(step1InitialValues || {});
    // Update real-time form values when initial values change (e.g., when navigating back)
    useEffect(()=>{
        if (step1InitialValues) {
            setRealTimeFormValues(step1InitialValues);
            // Update ref as well
            step1InitialValuesRef.current = step1InitialValues;
        }
    }, [
        step1InitialValues
    ]);
    const [step3InitialValues, setStep3InitialValues] = useState(()=>getFormDataFromStorage(2));
    const step3FormGuidence = {
        title: step3FormGuidanceTitle,
        description: step3FormGuidanceDescription
    };
    // Helper function to sanitize label for use as object key
    const sanitizeLabel = useCallback((label)=>{
        if (!label) return '';
        // Remove special characters, normalize spaces, and convert to a safe key format
        return label.trim().replace(/[^a-zA-Z0-9\s]/g, '') // Remove special characters
        .replace(/\s+/g, '_') // Replace spaces with underscores
        .toLowerCase();
    }, []);
    // Store step 2 calculated fee data in sessionStorage using the same hook (step index 1)
    const saveStep2DataToStorage = useCallback((data, transactionTypePrefix)=>{
        // Get existing step2Data to merge with new data
        const existingStep2Data = getFormDataFromStorage(1) || {};
        const step2Data = {
            ...existingStep2Data
        };
        // Use transaction type prefix for keys (e.g., "buying_" or "selling_")
        const prefix = transactionTypePrefix ? `${transactionTypePrefix}_` : '';
        // Store feesInclVat with prefix
        data.feesInclVat.forEach((item)=>{
            if (item.label && !item.isTotal) {
                // Skip total items
                const sanitizedLabel = sanitizeLabel(item.label);
                if (sanitizedLabel) {
                    step2Data[`feesInclVat_${prefix}${sanitizedLabel}`] = item.amount.toString();
                }
            }
        });
        // Store mandatoryFees with prefix
        data.mandatoryFees.forEach((item)=>{
            if (item.label && !item.isTotal) {
                // Skip total items
                const sanitizedLabel = sanitizeLabel(item.label);
                if (sanitizedLabel) {
                    step2Data[`mandatoryFees_${prefix}${sanitizedLabel}`] = item.amount.toString();
                }
            }
        });
        // Store additionalFees with prefix
        data.additionalFees.forEach((item)=>{
            if (item.label) {
                const sanitizedLabel = sanitizeLabel(item.label);
                if (sanitizedLabel) {
                    step2Data[`additionalFees_${prefix}${sanitizedLabel}`] = item.amount.toString();
                }
            }
        });
        // Store total amount with prefix if provided
        if (transactionTypePrefix) {
            step2Data[`totalAmount_${transactionTypePrefix}`] = data.totalAmount.toString();
        } else {
            step2Data['totalAmount'] = data.totalAmount.toString();
        }
        saveFormDataToStorage(1, step2Data);
    }, [
        saveFormDataToStorage,
        sanitizeLabel,
        getFormDataFromStorage
    ]);
    let step1FormFields, step1FormGuidence, step3FormFields;
    const getFeeByTransactionType = useCallback((feeList, feeTransactionType)=>{
        if (!feeList?.length) {
            return [];
        }
        return feeList.filter((fee)=>{
            const transactionType = fee.fields?.transactionType;
            return transactionType?.fields?.code?.value === feeTransactionType;
        });
    }, []);
    /**
   * Gets the land registry fee based on transaction type and property amount.
   * Finds the fee where:
   * - The transactionType array contains an item with matching code
   * - The propertyAmount falls between priceFrom and priceTo
   * @param feeList Array of LandRegistryFee_Item
   * @param feeTransactionType Transaction type code (e.g., 'buy', 'sell', 'remortgage')
   * @param propertyAmount Property amount to match against priceFrom and priceTo
   * @returns The matching LandRegistryFee_Item or undefined
   */ const getLandRegistryFeeByTransactionType = useCallback((feeList, feeTransactionType, propertyAmount)=>{
        if (!feeList || !Array.isArray(feeList) || propertyAmount === undefined || propertyAmount === null) {
            return undefined;
        }
        const numericPropertyAmount = Number(propertyAmount);
        if (isNaN(numericPropertyAmount)) {
            return undefined;
        }
        return feeList.find((fee)=>{
            // Check if transactionType array contains matching code
            const transactionTypes = fee.fields?.transactionType;
            const hasMatchingTransactionType = transactionTypes?.some((type)=>type.fields?.code?.value === feeTransactionType);
            if (!hasMatchingTransactionType) {
                return false;
            }
            // Check if propertyAmount falls between priceFrom and priceTo
            const priceFrom = fee.fields?.priceFrom?.value;
            const priceTo = fee.fields?.priceTo?.value;
            if (priceFrom === undefined || priceTo === undefined) {
                return false;
            }
            return numericPropertyAmount >= priceFrom && numericPropertyAmount <= priceTo;
        });
    }, []);
    const getLeaseholdFeeByTransactionType = useCallback((feeList, feeTransactionType, propertyType)=>{
        if (!feeList || !Array.isArray(feeList) || propertyType === undefined || propertyType === null || propertyType === '' || !feeTransactionType) {
            return undefined;
        }
        return feeList.find((fee)=>{
            // Check if transactionType matches
            const transactionType = fee.fields?.transactionType;
            const hasMatchingTransactionType = transactionType?.fields?.code?.value === feeTransactionType;
            if (!hasMatchingTransactionType) {
                return false;
            }
            // Check if propertyType matches
            const feePropertyType = fee.fields?.propertyType?.fields?.key && 'value' in fee.fields?.propertyType?.fields?.key ? fee.fields?.propertyType?.fields?.key.value?.toLocaleLowerCase() : '';
            return propertyType.toLocaleLowerCase() === feePropertyType;
        });
    }, []);
    const getTotalAmount = useCallback((items)=>{
        if (!items || items.length === 0 || items === null) return 0;
        return items.reduce((total, item)=>{
            return total + Number(item?.amount ?? 0);
        }, 0);
    }, []);
    /**
   * Calculates the VAT amount given a total (gross) amount and VAT percentage.
   * @param totalAmount number|string - The total amount (inclusive of VAT if coming from "total", otherwise net).
   * @param vatPercent number|string - The VAT percentage (e.g., 20 for 20%).
   * @returns number - The VAT amount.
   */ const calculateVatAmount = useCallback((totalAmount, vatPercent)=>{
        if (isNaN(Number(totalAmount)) || isNaN(Number(vatPercent)) || Number(vatPercent) <= 0) return 0;
        return Number(totalAmount) * Number(vatPercent) / 100;
    }, []);
    // Helper function to build fees excluding VAT
    const buildFeesExclVat = useCallback((transactionType, formData, fieldMappings)=>{
        const legalFeeItem = getFeeByTransactionType(legalFee, transactionType)?.[0];
        const fees = [
            {
                label: legalFeeItem?.fields?.feeLabel?.value,
                amount: legalFeeItem?.fields?.amount?.value ?? 0,
                feeTooltipTitle: legalFeeItem?.fields?.feeTooltipTitle,
                feeTooltip: legalFeeItem?.fields?.feeTooltip
            }
        ];
        // Add leasehold fee if applicable
        if (formData?.[fieldMappings.propertyTypeField]?.toLocaleLowerCase() === 'leasehold') {
            const leaseholdFeeItem = getLeaseholdFeeByTransactionType(leaseholdFee, transactionType, formData?.[fieldMappings.transactionTypeField]?.toLocaleLowerCase() ?? '');
            fees.push({
                label: leaseholdFeeItem?.fields?.feeLabel?.value ?? '',
                amount: leaseholdFeeItem?.fields?.amount?.value ?? 0,
                feeTooltipTitle: leaseholdFeeItem?.fields?.feeTooltipTitle,
                feeTooltip: leaseholdFeeItem?.fields?.feeTooltip
            });
        }
        // Add mortgage fee if applicable
        if (formData?.[fieldMappings.mortgageField]?.toLocaleLowerCase() === 'yes') {
            const mortgageFeeItem = getFeeByTransactionType(mortgageFee, transactionType)?.[0];
            fees.push({
                label: mortgageFeeItem?.fields?.feeLabel?.value,
                amount: mortgageFeeItem?.fields?.amount?.value ?? 0,
                feeTooltipTitle: mortgageFeeItem?.fields?.feeTooltipTitle,
                feeTooltip: mortgageFeeItem?.fields?.feeTooltip
            });
        }
        return fees;
    }, [
        legalFee,
        mortgageFee,
        leaseholdFee,
        getFeeByTransactionType,
        getLeaseholdFeeByTransactionType
    ]);
    // Helper function to build mandatory fees
    const buildMandatoryFees = useCallback((transactionType, formData, config)=>{
        const mandatorySearchesItem = getFeeByTransactionType(mandatoryFeeSearches, transactionType)?.[0];
        const fees = [
            {
                label: mandatorySearchesItem?.fields?.feeLabel?.value ?? '',
                amount: mandatorySearchesItem?.fields?.amount?.value ?? 0,
                feeTooltipTitle: mandatorySearchesItem?.fields?.feeTooltipTitle,
                feeTooltip: mandatorySearchesItem?.fields?.feeTooltip
            }
        ];
        // Add no search policy fee for remortgage
        if (config.includeNoSearchPolicy && noSearchPolicyLabel && noSearchPolicyFee) {
            fees.push({
                label: noSearchPolicyLabel.value ?? '',
                amount: noSearchPolicyFee.value ?? 0,
                feeTooltipTitle: noSearchPolicyFeeTooltipTitle,
                feeTooltip: noSearchPolicyFeeTooltip
            });
        }
        // Add land registry fee if applicable
        if (landRegistryFeeLabel && config.landRegistryFeeTooltip) {
            const landRegistryFeeItem = getLandRegistryFeeByTransactionType(landRegistryFee, transactionType, formData?.[config.propertyAmountField]);
            fees.push({
                label: landRegistryFeeLabel.value ?? '',
                amount: landRegistryFeeItem?.fields?.amount?.value ?? 0,
                feeTooltipTitle: config.landRegistryFeeTooltipTitle,
                feeTooltip: config.landRegistryFeeTooltip
            });
        }
        // Add telegraphic transfer fee
        if (telegraphicTransferFeeLabel && telegraphicTransferFee && telegraphicTransferFeeTooltip) {
            fees.push({
                label: telegraphicTransferFeeLabel.value ?? '',
                amount: (telegraphicTransferFee.value ?? 0) + calculateVatAmount(telegraphicTransferFee.value ?? 0, vatFeePercent?.value ?? 0),
                feeTooltipTitle: telegraphicTransferFeeTooltipTitle,
                feeTooltip: telegraphicTransferFeeTooltip
            });
        }
        return fees;
    }, [
        getFeeByTransactionType,
        mandatoryFeeSearches,
        noSearchPolicyLabel,
        noSearchPolicyFee,
        landRegistryFeeLabel,
        telegraphicTransferFeeLabel,
        telegraphicTransferFee,
        telegraphicTransferFeeTooltip,
        noSearchPolicyFeeTooltipTitle,
        noSearchPolicyFeeTooltip,
        getLandRegistryFeeByTransactionType,
        landRegistryFee,
        calculateVatAmount,
        vatFeePercent?.value,
        telegraphicTransferFeeTooltipTitle
    ]);
    // Helper function to replace {{propertyAmount}} token in guidance block description
    const replacePropertyAmountToken = useCallback((guidanceBlock, transactionType)=>{
        if (!guidanceBlock?.description?.value || "object" === 'undefined') {
            return guidanceBlock;
        }
        try {
            // Get form data from session storage at index 0
            const formData = getFormDataFromStorage(0);
            if (!formData) {
                return guidanceBlock;
            }
            // Determine which property amount field to use based on transaction type
            let propertyAmountKey;
            switch(transactionType){
                case 'remortgage':
                    propertyAmountKey = 'remortgageAmount';
                    break;
                case 'buy':
                    propertyAmountKey = 'propertyAmountBuying';
                    break;
                case 'sell':
                    propertyAmountKey = 'propertyAmountSelling';
                    break;
                default:
                    propertyAmountKey = 'propertyAmountBuying';
            }
            // Get the property amount value from form data
            const propertyAmount = formData[propertyAmountKey];
            if (!propertyAmount) {
                return guidanceBlock;
            }
            // Format the amount as currency
            const formatCurrency = (value)=>{
                const numValue = typeof value === 'string' ? parseFloat(value.replace(/[£,]/g, '')) : value;
                if (isNaN(numValue)) return value.toString();
                return new Intl.NumberFormat('en-GB', {
                    style: 'currency',
                    currency: 'GBP',
                    minimumFractionDigits: 2
                }).format(numValue);
            };
            const formattedAmount = formatCurrency(propertyAmount);
            // Replace {{propertyAmount}} token in description
            const updatedDescription = guidanceBlock.description.value.replace(/\{\{propertyAmount\}\}/g, formattedAmount);
            // Return new guidance block with updated description
            return {
                ...guidanceBlock,
                description: {
                    ...guidanceBlock.description,
                    value: updatedDescription
                }
            };
        } catch (error) {
            console.error('Failed to replace propertyAmount token:', error);
            return guidanceBlock;
        }
    }, [
        getFormDataFromStorage
    ]);
    // Map selectedOption to readable transaction type text
    const getTransactionTypeText = (option)=>{
        if (!option) return 'buying';
        const normalizedOption = option.toLowerCase();
        switch(normalizedOption){
            case 'buy':
                return 'purchase';
            case 'sell':
                return 'sale';
            case 'remortgage':
                return 'remortgage';
            case 'buyandsell':
                return 'purchase and sale';
            default:
                return 'purchase';
        }
    };
    // Helper function to replace {{transactionType}} token in a string
    const replaceTransactionTypeToken = useCallback((text, selectedOptionValue)=>{
        if (!text) {
            return '';
        }
        const transactionTypeText = getTransactionTypeText(selectedOptionValue);
        // Replace {{transactionType}} token in the string
        return text.replace(/\{\{transactionType\}\}/g, transactionTypeText);
    }, []);
    // Helper function to replace {{emailAddress}} token in a string
    const replaceEmailAddressToken = useCallback((text)=>{
        if (!text) {
            return '';
        }
        try {
            // Get form data from session storage at index 2 (step 3 form data)
            const formData = getFormDataFromStorage(2);
            if (!formData) {
                return text;
            }
            // Get the email address from form data
            // Try common email field names
            const emailAddress = formData.Email;
            if (!emailAddress) {
                return text;
            }
            // Replace {{emailAddress}} token in the string
            return text.replace(/\{\{emailAddress\}\}/g, emailAddress);
        } catch (error) {
            console.error('Failed to replace emailAddress token:', error);
            return text;
        }
    }, [
        getFormDataFromStorage
    ]);
    const capitalizeEachWord = useCallback((str)=>{
        // Convert the entire string to lowercase to ensure consistency
        // Then split the string into an array of words
        const words = str.toLowerCase().split(' ');
        // Iterate over each word in the array and capitalize its first letter
        const capitalizedWords = words.map((word)=>{
            // Handle potential empty strings if multiple spaces exist
            if (word.length === 0) return '';
            return word.charAt(0).toUpperCase() + word.slice(1);
        });
        // Join the modified words back into a single string with spaces
        return capitalizedWords.join(' ');
    }, []);
    // Helper function to replace multiple tokens in adminEmailBody with data from step 3 form
    const replaceAdminEmailBodyTokens = useCallback((emailBody, step3FormData)=>{
        if (!emailBody || "object" === 'undefined') {
            return emailBody || '';
        }
        try {
            // Use provided step3FormData or try to get from form directly
            const formData = step3FormData || step3Ref.current?.getFormValues() || {};
            if (!formData || Object.keys(formData).length === 0) {
                return emailBody;
            }
            // Get step 2 data for quote details
            const step2FormData = getFormDataFromStorage(1);
            let processedBody = emailBody;
            // Map of token names to form field names
            const tokenMappings = {
                customerTitle: 'title',
                customerFirstName: 'firstName',
                customerLastName: 'lastName',
                customerTelephoneNumber: 'telephone',
                customerEmail: 'Email',
                customerComments: 'description',
                customerAddressLine1: 'postcodeBuildingAndStreet',
                customerAddressLine2: 'postcodeTownOrCity',
                customerAddressLine3: 'postcodeCounty',
                customerPostcode: 'postcode'
            };
            // Replace customer-related tokens
            Object.entries(tokenMappings).forEach(([tokenName, fieldName])=>{
                const tokenPattern = new RegExp(`\\{\\{${tokenName}\\}\\}`, 'g');
                const fieldValue = formData[fieldName] || '';
                processedBody = processedBody.replace(tokenPattern, fieldValue);
            });
            // Handle property address tokens - format property address details based on transaction type
            const formatPropertyAddress = (transactionType)=>{
                const propertyAddressFields1 = [
                    transactionType === 'buying' && 'propertyAddressBuildingAndStreetBuying',
                    transactionType === 'selling' && 'propertyAddressBuildingAndStreetSelling',
                    transactionType === 'remortgage' && 'propertyAddressBuildingAndStreetRemortgage'
                ].filter(Boolean);
                const propertyAddressFields2 = [
                    transactionType === 'buying' && 'propertyAddressTownOrCityBuying',
                    transactionType === 'selling' && 'propertyAddressTownOrCitySelling',
                    transactionType === 'remortgage' && 'propertyAddressTownOrCityRemortgage'
                ].filter(Boolean);
                const propertyAddressFields3 = [
                    transactionType === 'buying' && 'propertyAddressCountyBuying',
                    transactionType === 'selling' && 'propertyAddressCountySelling',
                    transactionType === 'remortgage' && 'propertyAddressCountyRemortgage'
                ].filter(Boolean);
                const propertyAddressFieldsPostcode = [
                    transactionType === 'buying' && 'propertyAddressBuying',
                    transactionType === 'selling' && 'propertyAddressSelling',
                    transactionType === 'remortgage' && 'propertyAddressRemortgage'
                ].filter(Boolean);
                // Find which property address field has data
                let propertyAddressField1 = '';
                for (const field of propertyAddressFields1){
                    if (formData[field]) {
                        propertyAddressField1 = field;
                        break;
                    }
                }
                let propertyAddressField2 = '';
                for (const field of propertyAddressFields2){
                    if (formData[field]) {
                        propertyAddressField2 = field;
                        break;
                    }
                }
                let propertyAddressField3 = '';
                for (const field of propertyAddressFields3){
                    if (formData[field]) {
                        propertyAddressField3 = field;
                        break;
                    }
                }
                let propertyAddressFieldPostcode = '';
                for (const field of propertyAddressFieldsPostcode){
                    if (formData[field]) {
                        propertyAddressFieldPostcode = field;
                        break;
                    }
                }
                // Parse property address fields
                let propertyAddressLine1 = '';
                let propertyAddressLine2 = '';
                let propertyAddressLine3 = '';
                let propertyPostcode = '';
                if (propertyAddressField1) {
                    try {
                        const propertyAddress = JSON.parse(formData[propertyAddressField1]);
                        propertyAddressLine1 = propertyAddress.line1 || propertyAddress || '';
                    } catch  {
                        propertyAddressLine1 = formData[propertyAddressField1] || '';
                    }
                }
                if (propertyAddressField2) {
                    try {
                        const propertyAddress = JSON.parse(formData[propertyAddressField2]);
                        propertyAddressLine2 = propertyAddress.line2 || propertyAddress || '';
                    } catch  {
                        propertyAddressLine2 = formData[propertyAddressField2] || '';
                    }
                }
                if (propertyAddressField3) {
                    try {
                        const propertyAddress = JSON.parse(formData[propertyAddressField3]);
                        propertyAddressLine3 = propertyAddress.line3 || propertyAddress || '';
                    } catch  {
                        propertyAddressLine3 = formData[propertyAddressField3] || '';
                    }
                }
                if (propertyAddressFieldPostcode) {
                    try {
                        const propertyAddress = JSON.parse(formData[propertyAddressFieldPostcode]);
                        propertyPostcode = propertyAddress.postcode || propertyAddress || '';
                    } catch  {
                        propertyPostcode = formData[propertyAddressFieldPostcode] || '';
                    }
                }
                // Format address as HTML table (similar to quote details)
                if (!propertyAddressLine1 && !propertyAddressLine2 && !propertyAddressLine3 && !propertyPostcode) {
                    return '';
                }
                let addressTable = '';
                addressTable += '<table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="border-collapse: collapse; border-spacing: 0;">';
                if (propertyAddressLine1) {
                    addressTable += `<tr><td style="padding: 8px 0; font-size: 16px; font-weight: 600; width: 50%">Property Address Line 1:</td>`;
                    addressTable += `<td style="padding: 8px 0; font-size: 16px; color: #0c1b39">${propertyAddressLine1}</td></tr>`;
                }
                if (propertyAddressLine2) {
                    addressTable += `<tr><td style="padding: 8px 0; font-size: 16px; font-weight: 600; width: 50%">Property Address line 2:</td>`;
                    addressTable += `<td style="padding: 8px 0; font-size: 16px; color: #0c1b39">${propertyAddressLine2}</td></tr>`;
                }
                if (propertyAddressLine3) {
                    addressTable += `<tr><td style="padding: 8px 0; font-size: 16px; font-weight: 600; width: 50%">Property Address line 3:</td>`;
                    addressTable += `<td style="padding: 8px 0; font-size: 16px; color: #0c1b39">${propertyAddressLine3}</td></tr>`;
                }
                if (propertyPostcode) {
                    addressTable += `<tr><td style="padding: 8px 0; font-size: 16px; font-weight: 600; width: 50%">Property Address Postcode:</td>`;
                    addressTable += `<td style="padding: 8px 0; font-size: 16px; color: #0c1b39">${propertyPostcode}</td></tr>`;
                }
                addressTable += '<tr><td style="padding-bottom: 16px;" colspan="2"></td></tr>';
                addressTable += '</table>';
                return addressTable;
            };
            // Replace {{propertyAddressDetails}} token based on transaction type
            if (processedBody.includes('{{propertyAddressDetails}}')) {
                const normalizedSelectedOption = selectedOption?.toLowerCase();
                let propertyAddressDetails = '';
                if (normalizedSelectedOption === 'buyandsell') {
                    // For buyandsell, show both addresses in separate sections with headers
                    const buyingAddress = formatPropertyAddress('buying');
                    const sellingAddress = formatPropertyAddress('selling');
                    if (buyingAddress && sellingAddress) {
                        propertyAddressDetails = `<h2 style="font-size: 20px; font-weight: 600; color: #0c1b39; margin: 0 0 16px 0">Purchase Property Address</h2>${buyingAddress}<h2 style="font-size: 20px; font-weight: 600; color: #0c1b39; margin: 0 0 16px 0">Sale Property Address</h2>${sellingAddress}`;
                    } else if (buyingAddress) {
                        propertyAddressDetails = `<h2 style="font-size: 20px; font-weight: 600; color: #0c1b39; margin: 0 0 16px 0">Purchase Property Address</h2>${buyingAddress}`;
                    } else if (sellingAddress) {
                        propertyAddressDetails = `<h2 style="font-size: 20px; font-weight: 600; color: #0c1b39; margin: 0 0 16px 0">Sale Property Address</h2>${sellingAddress}`;
                    }
                } else {
                    // For single transaction types, determine which address to show
                    let transactionType = 'buying';
                    if (normalizedSelectedOption === 'sell') {
                        transactionType = 'selling';
                    } else if (normalizedSelectedOption === 'remortgage') {
                        transactionType = 'remortgage';
                    }
                    const address = formatPropertyAddress(transactionType);
                    if (address) {
                        const transactionLabel = transactionType === 'buying' ? 'Purchase' : transactionType === 'selling' ? 'Sale' : 'Remortgage';
                        propertyAddressDetails = `<h2 style="font-size: 20px; font-weight: 600; color: #0c1b39; margin: 0 0 16px 0">${transactionLabel} Property Address</h2>${address}`;
                    }
                }
                processedBody = processedBody.replace(/\{\{propertyAddressDetails\}\}/g, propertyAddressDetails);
            }
            // Handle quoteDetails token - generate HTML for quote details from step 2 data
            if (processedBody.includes('{{quoteDetails}}')) {
                let quoteDetailsHtml = '';
                if (step2FormData) {
                    // Get property value from step 1
                    const step1FormData = getFormDataFromStorage(0);
                    // Format currency
                    const formatCurrency = (value)=>{
                        const numValue = typeof value === 'string' ? parseFloat(value.replace(/[£,]/g, '')) : value;
                        if (isNaN(numValue)) return '£0.00';
                        return new Intl.NumberFormat('en-GB', {
                            style: 'currency',
                            currency: 'GBP',
                            minimumFractionDigits: 2
                        }).format(numValue);
                    };
                    // Helper function to generate quote details for a single transaction type
                    const generateQuoteDetailsSection = (transactionTypeLabel, propertyValue, feesPrefix = '')=>{
                        let sectionHtml = '';
                        // Section header
                        sectionHtml += `<h2 style="font-size:20px;font-weight:600;margin:0 0 16px 0;">${capitalizeEachWord(transactionTypeLabel)} Quote Details</h2>`;
                        // Property Value
                        sectionHtml += '<table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="border-collapse: collapse; border-spacing: 0;">';
                        sectionHtml += `<tr><td style="padding: 8px 0; font-size: 16px; font-weight: 600; width: 50%">Property Value:</td>`;
                        sectionHtml += `<td style="padding: 8px 0; font-size: 16px">${formatCurrency(propertyValue)}</td></tr>`;
                        sectionHtml += '<tr><td style="padding-bottom: 16px;" colspan="2"></td></tr>';
                        sectionHtml += '</table>';
                        // Filter fees based on prefix (for buyandsell, fees have buying/selling prefixes)
                        const filterKeysByPrefix = (keys, prefix)=>{
                            if (!prefix) return keys;
                            const normalizedPrefix = prefix.toLowerCase();
                            const isPurchase = normalizedPrefix === 'buying' || normalizedPrefix === 'purchase' || normalizedPrefix === 'buy';
                            const isSale = normalizedPrefix === 'selling' || normalizedPrefix === 'sale' || normalizedPrefix === 'sell';
                            return keys.filter((key)=>{
                                const lowerKey = key.toLowerCase();
                                // For buyandsell, fees are stored with prefixes like "feesInclVat_buying_" or "feesInclVat_selling_"
                                // Match keys that contain the transaction type prefix
                                if (isPurchase) {
                                    // Match purchase/buying related keys (with prefix), but exclude selling/sale keys
                                    return lowerKey.includes(`_${normalizedPrefix}_`) || lowerKey.includes('purchase') || lowerKey.includes('buying') || lowerKey.includes('buy') && !lowerKey.includes('selling') && !lowerKey.includes('sale');
                                } else if (isSale) {
                                    // Match selling/sale related keys (with prefix), but exclude purchase/buying keys
                                    return lowerKey.includes(`_${normalizedPrefix}_`) || lowerKey.includes('selling') || lowerKey.includes('sale') || lowerKey.includes('sell') && !lowerKey.includes('purchase') && !lowerKey.includes('buying') && !lowerKey.includes('buy');
                                }
                                // Fallback: include keys that match the prefix
                                return lowerKey.includes(normalizedPrefix);
                            });
                        };
                        // Get fees from step 2 data
                        const allFeesInclVatKeys = Object.keys(step2FormData).filter((key)=>key.startsWith('feesInclVat_'));
                        // For buyandsell, if filtering doesn't find fees, show all fees
                        let feesInclVatKeys = feesPrefix ? filterKeysByPrefix(allFeesInclVatKeys, feesPrefix) : allFeesInclVatKeys;
                        // If filtering resulted in no fees but we have fees available, show all fees
                        // This handles the case where fees don't have transaction type indicators
                        if (feesInclVatKeys.length === 0 && allFeesInclVatKeys.length > 0 && feesPrefix) {
                            feesInclVatKeys = allFeesInclVatKeys;
                        }
                        // Add Legal Fees section
                        if (feesInclVatKeys.length > 0) {
                            sectionHtml += '<h3 style="font-size: 18px; font-weight: 600; color: #0c1b39; margin: 0 0 16px 0">Legal Fees (exc. VAT)</h3>';
                            sectionHtml += '<table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="border-collapse: collapse; border-spacing: 0;">';
                            feesInclVatKeys.forEach((key)=>{
                                // Replace 'feesInclVat_buying_' or 'feesInclVat_selling_' with '' (remove the prefix if present)
                                const label = key.replace(/^feesInclVat_(buying_|selling_)?/, '').replace(/_/g, ' ');
                                const amount = step2FormData[key];
                                sectionHtml += `<tr><td style="padding: 8px 0; font-size: 16px; color: #0c1b39; font-weight: 600; width: 50%">${label === 'vat' ? 'VAT' : capitalizeEachWord(label)}:</td>`;
                                sectionHtml += `<td style="padding: 8px 0; font-size: 16px; color: #0c1b39">${formatCurrency(amount)}</td></tr>`;
                            });
                            sectionHtml += '<tr><td style="padding-bottom: 16px;" colspan="2"></td></tr>';
                            sectionHtml += '</table>';
                        }
                        // Get mandatory fees
                        const allMandatoryFeesKeys = Object.keys(step2FormData).filter((key)=>key.startsWith('mandatoryFees_'));
                        // For buyandsell, if filtering doesn't find fees, show all fees
                        let mandatoryFeesKeys = feesPrefix ? filterKeysByPrefix(allMandatoryFeesKeys, feesPrefix) : allMandatoryFeesKeys;
                        // If filtering resulted in no fees but we have fees available, show all fees
                        // This handles the case where fees don't have transaction type indicators
                        if (mandatoryFeesKeys.length === 0 && allMandatoryFeesKeys.length > 0 && feesPrefix) {
                            mandatoryFeesKeys = allMandatoryFeesKeys;
                        }
                        // Add Mandatory Fees section
                        if (mandatoryFeesKeys.length > 0) {
                            sectionHtml += '<h3 style="font-size: 18px; font-weight: 600; color: #0c1b39; margin: 0 0 16px 0">Mandatory Fees & Taxes</h3>';
                            sectionHtml += '<table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="border-collapse: collapse; border-spacing: 0;">';
                            mandatoryFeesKeys.forEach((key)=>{
                                const label = key.replace(/^mandatoryFees_(buying_|selling_)?/, '').replace(/_/g, ' ');
                                const amount = step2FormData[key];
                                sectionHtml += `<tr><td style="padding: 8px 0; font-size: 16px; color: #0c1b39; font-weight: 600; width: 50%">${capitalizeEachWord(label)}:</td>`;
                                sectionHtml += `<td style="padding: 8px 0; font-size: 16px; color: #0c1b39">${formatCurrency(amount)}</td></tr>`;
                            });
                            sectionHtml += '<tr><td style="padding-bottom: 16px;" colspan="2"></td></tr>';
                            sectionHtml += '</table>';
                        }
                        // Calculate total for this section
                        let sectionTotal = 0;
                        feesInclVatKeys.forEach((key)=>{
                            const amount = parseFloat(step2FormData[key] || '0');
                            if (!isNaN(amount)) sectionTotal += amount;
                        });
                        mandatoryFeesKeys.forEach((key)=>{
                            const amount = parseFloat(step2FormData[key] || '0');
                            if (!isNaN(amount)) sectionTotal += amount;
                        });
                        // Add Total Cost for this section
                        sectionHtml += '<table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="border-collapse: collapse; border-spacing: 0;">';
                        sectionHtml += `<tr><td style="padding: 12px 0; font-size: 20px; font-weight: 600; color: #0c1b39; width: 50%; border-top: 1px solid #0c1b39">Total Cost:</td>`;
                        sectionHtml += `<td style="padding: 12px 0; font-size: 20px; font-weight: 600; color: #0c1b39; border-top: 1px solid #0c1b39">${formatCurrency(sectionTotal)}</td></tr>`;
                        sectionHtml += '</table>';
                        return sectionHtml;
                    };
                    // Check if transaction type is buyandsell
                    const normalizedSelectedOption = selectedOption?.toLowerCase();
                    if (normalizedSelectedOption === 'buyandsell') {
                        // Generate separate sections for Purchase and Sale
                        const purchasePropertyValue = step1FormData?.['propertyAmountBuying'] || '0';
                        const salePropertyValue = step1FormData?.['propertyAmountSelling'] || '0';
                        // For buyandsell, try to filter fees, but if filtering results in no fees,
                        // show all fees in both sections (since fees might not have transaction type indicators)
                        const allFeesKeys = Object.keys(step2FormData).filter((key)=>key.startsWith('feesInclVat_') || key.startsWith('mandatoryFees_'));
                        // Check if fees can be distinguished by transaction type
                        const hasDistinguishableFees = allFeesKeys.some((key)=>{
                            const lowerKey = key.toLowerCase();
                            return lowerKey.includes('buying') || lowerKey.includes('purchase') || lowerKey.includes('selling') || lowerKey.includes('sale');
                        });
                        // If fees can't be distinguished, show all fees in both sections
                        // Otherwise, try to filter by transaction type
                        const purchasePrefix = hasDistinguishableFees ? 'buying' : '';
                        const salePrefix = hasDistinguishableFees ? 'selling' : '';
                        quoteDetailsHtml += generateQuoteDetailsSection('Purchase', purchasePropertyValue, purchasePrefix);
                        quoteDetailsHtml += '<table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="border-collapse: collapse; border-spacing: 0;"><tr><td style="border-bottom: 1px solid #0c1b39;" colspan="2"></td></tr><tr><td style="padding-bottom: 40px;" colspan="2"></td></tr></table>'; // Spacer between sections
                        quoteDetailsHtml += generateQuoteDetailsSection('Sale', salePropertyValue, salePrefix);
                    } else {
                        // Single transaction type - use existing logic
                        const transactionTypeText = getTransactionTypeText(selectedOption);
                        const propertyValue = step1FormData?.['propertyAmountBuying'] || step1FormData?.['propertyAmountSelling'] || step1FormData?.['remortgageAmount'] || '0';
                        quoteDetailsHtml += generateQuoteDetailsSection(transactionTypeText, propertyValue);
                    }
                }
                processedBody = processedBody.replace(/\{\{quoteDetails\}\}/g, quoteDetailsHtml);
            }
            return processedBody;
        } catch (error) {
            console.error('Failed to replace adminEmailBody tokens:', error);
            return emailBody;
        }
    }, [
        getFormDataFromStorage,
        selectedOption,
        step3Ref
    ]);
    const emailRelatedFields = useMemo(()=>{
        const tmpl = emailTemplate?.fields;
        const adminEmailBody = tmpl?.adminEmailBody;
        const adminSubject = tmpl?.adminSubject;
        const userEmailBody = tmpl?.userEmailBody;
        return {
            adminEmailBody: {
                ...adminEmailBody,
                value: adminEmailBody?.value || ''
            },
            adminFrom: tmpl?.adminFrom,
            adminSubject: {
                ...adminSubject,
                value: replaceTransactionTypeToken(adminSubject?.value, selectedOption)
            },
            adminTo: tmpl?.adminTo,
            userEmailBody: {
                ...userEmailBody,
                value: replaceTransactionTypeToken(userEmailBody?.value, selectedOption)
            },
            userFrom: tmpl?.userFrom,
            userSubject: tmpl?.userSubject
        };
    }, [
        emailTemplate,
        replaceTransactionTypeToken,
        selectedOption
    ]);
    // Helper function to render ConveyancingStep2 component
    const renderStep2Component = useCallback((key, guidanceBlock, additionalFeesList, tabTitle, feesInclVat, mandatoryFees, config, transactionType = 'buy')=>{
        // Replace {{propertyAmount}} token in guidance block description
        const updatedGuidanceBlock = replacePropertyAmountToken(guidanceBlock, transactionType);
        const filteredFeesItems = feesInclVat.filter((item)=>item !== undefined && item !== null).map((item)=>({
                label: item.label ?? '',
                amount: item.amount ?? 0,
                feeTooltipTitle: item.feeTooltipTitle,
                feeTooltip: item.feeTooltip
            }));
        return /*#__PURE__*/ _jsx(ConveyancingStep2, {
            guidanceBlock: updatedGuidanceBlock,
            additionalFeesBlock: {
                title: additionalFeesLabel,
                description: additionalFeesDescription,
                additionalFeesList
            },
            tabTitle: tabTitle,
            step2FeesExclVat: {
                title: config.feesTitle ?? '',
                items: [
                    ...filteredFeesItems,
                    {
                        label: totalFeesInclVatLabel?.value ?? '',
                        amount: getTotalAmount(filteredFeesItems),
                        isTotal: true
                    }
                ]
            },
            step2MandatoryFees: {
                title: config.mandatoryFeesTitle ?? '',
                items: [
                    ...mandatoryFees,
                    {
                        label: config.totalMandatoryFeesLabel ?? '',
                        amount: getTotalAmount(mandatoryFees),
                        isTotal: true
                    }
                ]
            },
            totalObj: {
                label: totalLabel?.value ?? '',
                amount: getTotalAmount(feesInclVat) + getTotalAmount(mandatoryFees)
            }
        }, key);
    }, [
        additionalFeesLabel,
        additionalFeesDescription,
        totalFeesInclVatLabel,
        totalLabel,
        getTotalAmount,
        replacePropertyAmountToken
    ]);
    // Helper function to build complete fee structure for a transaction type
    const buildTransactionFees = useCallback((transactionType, formData, fieldMappings, landRegistryFeeTooltipTitle, landRegistryFeeTooltip, includeNoSearchPolicy)=>{
        // Build fees excluding VAT
        const feesExclVat = buildFeesExclVat(transactionType, formData, {
            mortgageField: fieldMappings.mortgageField,
            propertyTypeField: fieldMappings.propertyTypeField,
            transactionTypeField: fieldMappings.transactionTypeField
        });
        // Filter and map fees excluding VAT
        const filteredFeesExclVat = feesExclVat.filter((item)=>item !== undefined && item !== null).map((item)=>({
                label: item?.label ?? '',
                amount: item?.amount ?? 0,
                feeTooltipTitle: item?.feeTooltipTitle,
                feeTooltip: item?.feeTooltip
            }));
        // Build fees including VAT
        const feesInclVat = [
            ...filteredFeesExclVat,
            {
                label: vatLabel?.value ?? '',
                amount: calculateVatAmount(getTotalAmount(filteredFeesExclVat), vatFeePercent?.value ?? 0),
                feeTooltipTitle: vatTooltipTitle,
                feeTooltip: vatTooltip
            }
        ];
        // Build mandatory fees
        const mandatoryFees = buildMandatoryFees(transactionType, formData, {
            propertyAmountField: fieldMappings.propertyAmountField,
            landRegistryFeeTooltipTitle,
            landRegistryFeeTooltip,
            includeNoSearchPolicy
        });
        return {
            feesInclVat,
            mandatoryFees
        };
    }, [
        buildFeesExclVat,
        vatLabel?.value,
        calculateVatAmount,
        getTotalAmount,
        vatFeePercent?.value,
        vatTooltipTitle,
        vatTooltip,
        buildMandatoryFees
    ]);
    // Calculate step 2 content based on form data - this will recalculate when step1FormData changes
    const conveyancingStep2Content = useMemo(()=>{
        switch(selectedOption?.toString().toLowerCase()){
            case 'buy':
                {
                    const { feesInclVat, mandatoryFees } = buildTransactionFees('buy', step1FormData, {
                        mortgageField: 'mortgageBuying',
                        propertyTypeField: 'propertyTypeBuying',
                        transactionTypeField: 'buying',
                        propertyAmountField: 'propertyAmountBuying'
                    }, landRegistryFeeBuyingTooltipTitle, landRegistryFeeBuyingTooltip);
                    return [
                        renderStep2Component('step-2-purchase', {
                            title: step2FormGuidanceBuyingTitle,
                            description: step2FormGuidanceBuyingDescription
                        }, getFeeByTransactionType(additionalFees, 'buy'), purchaseFeeTabLabel, feesInclVat, mandatoryFees, {
                            feesTitle: step2PurchaseFeesTitle?.value,
                            mandatoryFeesTitle: mandatoryFeesTitleBuying?.value,
                            totalMandatoryFeesLabel: totalPurchaseMandatoryFeeAndTaxesLabel?.value
                        }, 'buy')
                    ];
                }
            case 'sell':
                {
                    const { feesInclVat, mandatoryFees } = buildTransactionFees('sell', step1FormData, {
                        mortgageField: 'mortgageSelling',
                        propertyTypeField: 'propertyTypeSelling',
                        transactionTypeField: 'selling',
                        propertyAmountField: 'propertyAmountSelling'
                    });
                    return [
                        renderStep2Component('step-2-sell', {
                            title: step2FormGuidanceSellingTitle,
                            description: step2FormGuidanceSellingDescription
                        }, getFeeByTransactionType(additionalFees, 'sell'), salesFeeTabLabel, feesInclVat, mandatoryFees, {
                            feesTitle: step2SellingFeesTitle?.value,
                            mandatoryFeesTitle: mandatoryFeeTitleSelling?.value,
                            totalMandatoryFeesLabel: totalSellingMandatoryFeeAndTaxesLabel?.value
                        }, 'sell')
                    ];
                }
            case 'buyandsell':
                {
                    // Build fees for buying transaction
                    const buyFees = buildTransactionFees('buy', step1FormData, {
                        mortgageField: 'mortgageBuying',
                        propertyTypeField: 'propertyTypeBuying',
                        transactionTypeField: 'buying',
                        propertyAmountField: 'propertyAmountBuying'
                    }, landRegistryFeeBuyingTooltipTitle, landRegistryFeeBuyingTooltip);
                    // Build fees for selling transaction
                    const sellFees = buildTransactionFees('sell', step1FormData, {
                        mortgageField: 'mortgageSelling',
                        propertyTypeField: 'propertyTypeSelling',
                        transactionTypeField: 'selling',
                        propertyAmountField: 'propertyAmountSelling'
                    });
                    return [
                        renderStep2Component('step-2-purchase', {
                            title: step2FormGuidanceBuyingTitle,
                            description: step2FormGuidanceBuyingDescription
                        }, getFeeByTransactionType(additionalFees, 'buy'), purchaseFeeTabLabel, buyFees.feesInclVat, buyFees.mandatoryFees, {
                            feesTitle: step2PurchaseFeesTitle?.value,
                            mandatoryFeesTitle: mandatoryFeesTitleBuying?.value,
                            totalMandatoryFeesLabel: totalPurchaseMandatoryFeeAndTaxesLabel?.value
                        }, 'buy'),
                        renderStep2Component('step-2-sales', {
                            title: step2FormGuidanceSellingTitle,
                            description: step2FormGuidanceSellingDescription
                        }, getFeeByTransactionType(additionalFees, 'sell'), salesFeeTabLabel, sellFees.feesInclVat, sellFees.mandatoryFees, {
                            feesTitle: step2SellingFeesTitle?.value,
                            mandatoryFeesTitle: mandatoryFeeTitleSelling?.value,
                            totalMandatoryFeesLabel: totalSellingMandatoryFeeAndTaxesLabel?.value
                        }, 'sell')
                    ];
                }
            case 'remortgage':
                {
                    const { feesInclVat, mandatoryFees } = buildTransactionFees('remortgage', step1FormData, {
                        mortgageField: 'mortgageRemortgage',
                        propertyTypeField: 'propertyTypeRemortgage',
                        transactionTypeField: 'remortgage',
                        propertyAmountField: 'remortgageAmount'
                    }, landRegistryFeeRemortgageTooltipTitle, landRegistryFeeRemortgageTooltip, true // includeNoSearchPolicy
                    );
                    return [
                        renderStep2Component('step-2-remortgage', {
                            title: step2FormGuidanceRemortgageTitle,
                            description: step2FormGuidanceRemortgageDescription
                        }, getFeeByTransactionType(additionalFees, 'remortgage'), undefined, feesInclVat, mandatoryFees, {
                            feesTitle: step2RemortgageFeesTitle?.value,
                            mandatoryFeesTitle: mandatoryFeeTitleRemortgage?.value,
                            totalMandatoryFeesLabel: totalRemortgageMandatoryFeeAndTaxesLabel?.value
                        }, 'remortgage')
                    ];
                }
            default:
                const { feesInclVat, mandatoryFees } = buildTransactionFees('buy', step1FormData, {
                    mortgageField: 'mortgageBuying',
                    propertyTypeField: 'propertyTypeBuying',
                    transactionTypeField: 'buying',
                    propertyAmountField: 'propertyAmountBuying'
                }, landRegistryFeeBuyingTooltipTitle, landRegistryFeeBuyingTooltip);
                return [
                    renderStep2Component('step-2-purchase', {
                        title: step2FormGuidanceBuyingTitle,
                        description: step2FormGuidanceBuyingDescription
                    }, getFeeByTransactionType(additionalFees, 'buy'), purchaseFeeTabLabel, feesInclVat, mandatoryFees, {
                        feesTitle: step2PurchaseFeesTitle?.value,
                        mandatoryFeesTitle: mandatoryFeesTitleBuying?.value,
                        totalMandatoryFeesLabel: totalPurchaseMandatoryFeeAndTaxesLabel?.value
                    }, 'buy')
                ];
        }
    }, [
        selectedOption,
        buildTransactionFees,
        step1FormData,
        landRegistryFeeBuyingTooltipTitle,
        landRegistryFeeBuyingTooltip,
        renderStep2Component,
        step2FormGuidanceBuyingTitle,
        step2FormGuidanceBuyingDescription,
        getFeeByTransactionType,
        additionalFees,
        purchaseFeeTabLabel,
        step2PurchaseFeesTitle?.value,
        mandatoryFeesTitleBuying?.value,
        totalPurchaseMandatoryFeeAndTaxesLabel?.value,
        step2FormGuidanceSellingTitle,
        step2FormGuidanceSellingDescription,
        salesFeeTabLabel,
        step2SellingFeesTitle?.value,
        mandatoryFeeTitleSelling?.value,
        totalSellingMandatoryFeeAndTaxesLabel?.value,
        landRegistryFeeRemortgageTooltipTitle,
        landRegistryFeeRemortgageTooltip,
        step2FormGuidanceRemortgageTitle,
        step2FormGuidanceRemortgageDescription,
        step2RemortgageFeesTitle?.value,
        mandatoryFeeTitleRemortgage?.value,
        totalRemortgageMandatoryFeeAndTaxesLabel?.value
    ]);
    // Helper function to extract and save step 2 data
    const extractAndSaveStep2Data = useCallback(()=>{
        if (!step1FormData || !conveyancingStep2Content || conveyancingStep2Content.length === 0) {
            return;
        }
        // Extract fee data from all components in conveyancingStep2Content
        // For buyandsell, there will be two components (purchase and sale)
        conveyancingStep2Content.forEach((component, index)=>{
            if (/*#__PURE__*/ isValidElement(component)) {
                const props = component.props;
                if (props.step2FeesExclVat && props.step2MandatoryFees && props.totalObj) {
                    const feesInclVat = props.step2FeesExclVat.items || [];
                    const mandatoryFees = props.step2MandatoryFees.items || [];
                    const totalAmount = props.totalObj.amount || 0;
                    // Extract additional fees
                    const additionalFees = [];
                    if (props.additionalFeesBlock?.additionalFeesList) {
                        props.additionalFeesBlock.additionalFeesList.forEach((item)=>{
                            const label = item.fields?.feeLabel?.value ?? '';
                            const amount = item.fields?.amount?.value || item.fields?.feeVariableText?.value || 0;
                            if (label) {
                                additionalFees.push({
                                    label,
                                    amount
                                });
                            }
                        });
                    }
                    // Determine transaction type prefix based on component key or index
                    // For buyandsell: first component is purchase, second is sale
                    let transactionTypePrefix;
                    const normalizedSelectedOption = selectedOption?.toLowerCase();
                    if (normalizedSelectedOption === 'buyandsell') {
                        // Check component key to determine transaction type
                        if (props.key === 'step-2-purchase' || index === 0) {
                            transactionTypePrefix = 'buying';
                        } else if (props.key === 'step-2-sales' || props.key === 'step-2-sell' || index === 1) {
                            transactionTypePrefix = 'selling';
                        }
                    } else {
                        // For single transaction types, no prefix needed
                        transactionTypePrefix = undefined;
                    }
                    saveStep2DataToStorage({
                        feesInclVat: feesInclVat.map((item)=>({
                                label: item.label || '',
                                amount: item.amount || 0,
                                feeTooltipTitle: item.feeTooltipTitle,
                                feeTooltip: item.feeTooltip,
                                isTotal: item.isTotal
                            })),
                        mandatoryFees: mandatoryFees.map((item)=>({
                                label: item.label || '',
                                amount: item.amount || 0,
                                feeTooltipTitle: item.feeTooltipTitle,
                                feeTooltip: item.feeTooltip,
                                isTotal: item.isTotal
                            })),
                        additionalFees,
                        totalAmount
                    }, transactionTypePrefix);
                }
            }
        });
    }, [
        step1FormData,
        conveyancingStep2Content,
        saveStep2DataToStorage,
        selectedOption
    ]);
    const filteredStep3FormFields = useMemo(()=>(exceptField1, exceptField2)=>{
            const formFields = step3Form?.fields?.formFields;
            if (!Array.isArray(formFields)) return undefined;
            return formFields.filter((field)=>field?.fields?.fieldName?.value !== exceptField1 && field?.fields?.fieldName?.value !== exceptField2);
        }, [
        step3Form?.fields?.formFields
    ]);
    // Function to filter propertyTypeBuying and propertyTypeSelling options based on buying/selling field values
    const filterPropertyTypeOptions = useCallback((formFields, formData)=>{
        if (!formFields || !Array.isArray(formFields)) return formFields || [];
        // Check if "Flat" is selected in any buying or selling related field
        const hasFlatSelected = formData ? Object.entries(formData).some(([key, value])=>{
            // Check if the field name contains "buying" or "selling" and value is "Flat"
            const lowerKey = key.toLowerCase();
            const lowerValue = (value || '').toLowerCase().trim();
            return (lowerKey.includes('buying') || lowerKey.includes('selling')) && lowerValue === 'flat';
        }) : false;
        return formFields.map((field)=>{
            const fieldName = field.fields?.fieldName?.value;
            const fieldType = field.fields?.fieldType?.value?.toLowerCase();
            // Only process select/dropdown fields named propertyTypeBuying or propertyTypeSelling
            if ((fieldType === 'select' || fieldType === 'dropdown') && (fieldName === 'propertyTypeBuying' || fieldName === 'propertyTypeSelling')) {
                // Check if "Flat" is selected in the corresponding buying or selling field
                // For propertyTypeBuying, check if any buying-related field has "Flat"
                // For propertyTypeSelling, check if any selling-related field has "Flat"
                const isBuyingField = fieldName === 'propertyTypeBuying';
                const shouldFilter = hasFlatSelected && (isBuyingField ? Object.entries(formData || {}).some(([key, value])=>key.toLowerCase().includes('buying') && (value || '').toLowerCase().trim() === 'flat') : Object.entries(formData || {}).some(([key, value])=>key.toLowerCase().includes('selling') && (value || '').toLowerCase().trim() === 'flat'));
                // If "Flat" is selected, filter to only show "Leasehold" option and preselect it
                if (shouldFilter) {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const options = field.fields?.selectOptionsAndSection || [];
                    // Filter to only show "Leasehold" option
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const filteredOptions = options.filter((option)=>{
                        const optionValue = option.fields?.value?.value || '';
                        const optionText = option.fields?.text?.value || '';
                        // Check both value and text for "Leasehold" (case-insensitive)
                        return optionValue.toLowerCase() === 'leasehold' || optionText.toLowerCase() === 'leasehold';
                    });
                    // Find the "Leasehold" option to get its value for defaultValue
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const leaseholdOption = filteredOptions.find((option)=>{
                        const optionValue = option.fields?.value?.value || '';
                        const optionText = option.fields?.text?.value || '';
                        return optionValue.toLowerCase() === 'leasehold' || optionText.toLowerCase() === 'leasehold';
                    });
                    // Get the leasehold value (prefer value field, fallback to text, then hardcoded)
                    const leaseholdValue = leaseholdOption && (leaseholdOption.fields?.value?.value || leaseholdOption.fields?.text?.value || 'leasehold');
                    return {
                        ...field,
                        fields: {
                            ...field.fields,
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            selectOptionsAndSection: filteredOptions,
                            // Set defaultValue to preselect "Leasehold" when "Flat" is selected
                            defaultValue: leaseholdValue ? {
                                value: leaseholdValue
                            } : undefined
                        }
                    };
                } else {
                    // If "Flat" is not selected, don't modify defaultValue
                    // Let the initialValues prop handle prefilling from sessionStorage
                    return field;
                }
            }
            // Handle section fields recursively
            if (fieldType === 'section') {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const sectionFields = field.fields?.selectOptionsAndSection || [];
                const filteredSectionFields = filterPropertyTypeOptions(sectionFields, formData);
                return {
                    ...field,
                    fields: {
                        ...field.fields,
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        selectOptionsAndSection: filteredSectionFields
                    }
                };
            }
            return field;
        });
    }, []);
    switch(selectedOption?.toString().toLowerCase()){
        case 'buy':
            step1FormFields = [
                {
                    formTitle: '',
                    formFields: step1FormBuying?.fields?.formFields
                }
            ];
            step1FormGuidence = {
                title: step1FormGuidanceBuyingTitle,
                description: step1FormGuidanceBuyingDescription
            };
            step3FormFields = {
                ...(step3Form?.fields ?? {}),
                formFields: filteredStep3FormFields('propertyAddressRemortgage', 'propertyAddressSelling'),
                submitStatus: submitStatus
            };
            break;
        case 'sell':
            step1FormFields = [
                {
                    formTitle: '',
                    formFields: step1FormSelling?.fields?.formFields
                }
            ];
            step1FormGuidence = {
                title: step1FormGuidanceSellingTitle,
                description: step1FormGuidanceSellingDescription
            };
            step3FormFields = {
                ...(step3Form?.fields ?? {}),
                formFields: filteredStep3FormFields('propertyAddressRemortgage', 'propertyAddressBuying'),
                submitStatus: submitStatus
            };
            break;
        case 'buyandsell':
            step1FormFields = [
                {
                    formTitle: step1FormBuyingTitle?.value,
                    formFields: step1FormBuying?.fields?.formFields
                },
                {
                    formTitle: step1FormSellingTitle?.value,
                    formFields: step1FormSelling?.fields?.formFields
                }
            ];
            step1FormGuidence = {
                title: step1FormGuidanceBuyingAndSellingTitle,
                description: step1FormGuidanceBuyingAndSellingDescription
            };
            step3FormFields = {
                ...(step3Form?.fields ?? {}),
                formFields: filteredStep3FormFields('propertyAddressRemortgage', 'undefined'),
                submitStatus: submitStatus
            };
            break;
        case 'remortgage':
            step1FormFields = [
                {
                    formTitle: '',
                    formFields: step1FormRemortgage?.fields?.formFields
                }
            ];
            step1FormGuidence = {
                title: step1FormGuidanceRemortgageTitle,
                description: step1FormGuidanceRemortgageDescription
            };
            step3FormFields = {
                ...(step3Form?.fields ?? {}),
                formFields: filteredStep3FormFields('propertyAddressBuying', 'propertyAddressSelling'),
                submitStatus: submitStatus
            };
            break;
        default:
            step1FormFields = [
                {
                    formTitle: '',
                    formFields: step1FormBuying?.fields?.formFields
                }
            ];
            step1FormGuidence = {
                title: step1FormGuidanceBuyingTitle,
                description: step1FormGuidanceBuyingDescription
            };
            step3FormFields = {
                ...(step3Form?.fields ?? {}),
                formFields: filteredStep3FormFields('propertyAddressRemortgage', 'propertyAddressSelling'),
                submitStatus: submitStatus
            };
            break;
    }
    // Track validation errors for each step
    const [stepErrors, setStepErrors] = useState(()=>{
        const initialErrors = {};
        defaultSteps.forEach((_, index)=>{
            initialErrors[index] = false;
        });
        return initialErrors;
    });
    const handleStepValidationChange = useCallback((stepIndex)=>{
        return (hasErrors)=>{
            // Defer state update to avoid updating during render
            setTimeout(()=>{
                setStepErrors((prev)=>({
                        ...prev,
                        [stepIndex]: hasErrors
                    }));
            }, 0);
        };
    }, []);
    // Function to clear all session storage including postcode fields
    const clearAllSessionStorage = useCallback(()=>{
        if ("object" === 'undefined') return;
        try {
            // Clear conveyancing form data
            clearAllFormDataFromStorage();
            // Clear all conveyancing-related session storage keys
            const keysToRemove = [
                'conveyancing-steps',
                'conveyancing-selected-option',
                'conveyancing-reset-key'
            ];
            keysToRemove.forEach((key)=>{
                window.sessionStorage.removeItem(key);
            });
            // Clear all postcode-related storage keys
            // Postcode fields use keys like 'postcode-addresses-${fieldName}'
            const allKeys = Object.keys(window.sessionStorage);
            allKeys.forEach((key)=>{
                if (key.startsWith('postcode-addresses-')) {
                    window.sessionStorage.removeItem(key);
                }
            });
        } catch (error) {
            console.error('Failed to clear session storage:', error);
        }
    }, [
        clearAllFormDataFromStorage
    ]);
    // Apply filtering to step1FormFields based on buying/selling field values (using real-time values)
    const filteredStep1FormFieldsWithPropertyType = useMemo(()=>{
        if (!step1FormFields) return step1FormFields;
        // Use real-time form values if available, otherwise fall back to step1FormData
        // Note: step1InitialValues is used by the form component directly, not here for filtering
        const formValuesForFiltering = Object.keys(realTimeFormValues).length > 0 ? realTimeFormValues : step1FormData || {};
        return step1FormFields.map((fieldGroup)=>{
            const rawFormFields = fieldGroup.formFields;
            const formFieldsForFilter = Array.isArray(rawFormFields) ? rawFormFields : undefined;
            return {
                ...fieldGroup,
                formFields: filterPropertyTypeOptions(formFieldsForFilter, formValuesForFiltering)
            };
        });
    }, [
        step1FormFields,
        realTimeFormValues,
        step1FormData,
        filterPropertyTypeOptions
    ]);
    // Track previous values to prevent infinite loops
    const prevPropertyTypeBuyingRef = useRef(undefined);
    const prevPropertyTypeSellingRef = useRef(undefined);
    const prevHasFlatBuyingRef = useRef(false);
    const prevHasFlatSellingRef = useRef(false);
    // Use ref to access current step1InitialValues without adding it to dependencies
    const step1InitialValuesRef = useRef(null);
    // Track if refs have been initialized
    const refsInitializedRef = useRef(false);
    // Effect to preselect "Leasehold" when "Flat" is selected
    useEffect(()=>{
        if ("object" === 'undefined') return;
        if (!step1Ref.current) return;
        // Use real-time form values to check for "Flat" selection
        const formValuesForChecking = Object.keys(realTimeFormValues).length > 0 ? realTimeFormValues : step1FormData;
        if (!formValuesForChecking) return;
        // Skip if this is the initial load and we don't have form values yet
        // This prevents interference with prefilling from sessionStorage
        const hasFormValues = Object.keys(formValuesForChecking).length > 0;
        if (!hasFormValues) return;
        // Initialize refs on first run based on current form data
        if (!refsInitializedRef.current) {
            const hasFlatBuyingInit = Object.entries(formValuesForChecking).some(([key, value])=>key.toLowerCase().includes('buying') && !key.toLowerCase().includes('propertytype') && (value || '').toLowerCase().trim() === 'flat');
            const hasFlatSellingInit = Object.entries(formValuesForChecking).some(([key, value])=>key.toLowerCase().includes('selling') && !key.toLowerCase().includes('propertytype') && (value || '').toLowerCase().trim() === 'flat');
            prevHasFlatBuyingRef.current = hasFlatBuyingInit;
            prevHasFlatSellingRef.current = hasFlatSellingInit;
            refsInitializedRef.current = true;
        }
        // Check if "Flat" is selected in buying or selling fields (excluding propertyType fields)
        const hasFlatBuying = Object.entries(formValuesForChecking).some(([key, value])=>key.toLowerCase().includes('buying') && !key.toLowerCase().includes('propertytype') && (value || '').toLowerCase().trim() === 'flat');
        const hasFlatSelling = Object.entries(formValuesForChecking).some(([key, value])=>key.toLowerCase().includes('selling') && !key.toLowerCase().includes('propertytype') && (value || '').toLowerCase().trim() === 'flat');
        // Find "Leasehold" value from filtered fields
        const findLeaseholdValue = ()=>{
            if (!filteredStep1FormFieldsWithPropertyType) return null;
            for (const fieldGroup of filteredStep1FormFieldsWithPropertyType){
                for (const field of fieldGroup.formFields || []){
                    const fieldName = field.fields?.fieldName?.value;
                    if (fieldName === 'propertyTypeBuying' || fieldName === 'propertyTypeSelling') {
                        // Check defaultValue first
                        if (field.fields?.defaultValue?.value) {
                            return field.fields.defaultValue.value;
                        }
                        // Check options
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        const options = field.fields?.selectOptionsAndSection || [];
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        const leaseholdOption = options.find((option)=>{
                            const optionValue = option.fields?.value?.value || '';
                            const optionText = option.fields?.text?.value || '';
                            return optionValue.toLowerCase() === 'leasehold' || optionText.toLowerCase() === 'leasehold';
                        });
                        if (leaseholdOption) {
                            return leaseholdOption.fields?.value?.value || leaseholdOption.fields?.text?.value || 'leasehold';
                        }
                    }
                }
            }
            return 'leasehold'; // Fallback
        };
        const leaseholdValue = findLeaseholdValue();
        if (!leaseholdValue) return;
        // Get current Formik values to check what's already set
        const currentFormikValues = step1Ref.current?.getFormValues() || {};
        // Get current initial values from ref (to avoid dependency on step1InitialValues)
        const currentInitialValues = step1InitialValuesRef.current || {};
        // Merge current Formik values with initial values to preserve all user selections
        const allCurrentValues = {
            ...currentInitialValues,
            ...currentFormikValues
        };
        const currentPropertyTypeBuying = allCurrentValues['propertyTypeBuying'];
        const currentPropertyTypeSelling = allCurrentValues['propertyTypeSelling'];
        // Update propertyTypeBuying if "Flat" is selected in buying fields
        if (hasFlatBuying) {
            // Always update ref to track that "Flat" is selected
            if (prevHasFlatBuyingRef.current !== hasFlatBuying) {
                prevHasFlatBuyingRef.current = hasFlatBuying;
            }
            // Only update propertyType if not already set to leasehold and state has changed
            if (currentPropertyTypeBuying !== leaseholdValue && prevPropertyTypeBuyingRef.current !== leaseholdValue) {
                // Update via initialValues - merge with existing values to preserve other fields including "Flat"
                setStep1InitialValues((prev)=>{
                    if (!prev) return {
                        ...allCurrentValues,
                        propertyTypeBuying: leaseholdValue
                    };
                    return {
                        ...prev,
                        ...allCurrentValues,
                        propertyTypeBuying: leaseholdValue
                    };
                });
                prevPropertyTypeBuyingRef.current = leaseholdValue;
            }
        } else {
            // Reset if "Flat" is deselected and currently set to leasehold
            // BUT only if "Flat" was previously selected (prevHasFlatBuyingRef.current === true)
            // This means leasehold was set programmatically, so we should reset it
            if (currentPropertyTypeBuying === leaseholdValue && prevHasFlatBuyingRef.current === true && // Was previously selected
            hasFlatBuying === false // Now deselected
            ) {
                setStep1InitialValues((prev)=>{
                    if (!prev) return prev;
                    const updated = {
                        ...prev,
                        ...allCurrentValues
                    };
                    updated.propertyTypeBuying = '';
                    return updated;
                });
                prevPropertyTypeBuyingRef.current = '';
                prevHasFlatBuyingRef.current = hasFlatBuying;
            } else if (prevHasFlatBuyingRef.current !== hasFlatBuying) {
                // Update ref to track state change even if we don't reset
                prevHasFlatBuyingRef.current = hasFlatBuying;
            }
        }
        // Update propertyTypeSelling if "Flat" is selected in selling fields
        if (hasFlatSelling) {
            // Always update ref to track that "Flat" is selected
            if (prevHasFlatSellingRef.current !== hasFlatSelling) {
                prevHasFlatSellingRef.current = hasFlatSelling;
            }
            // Only update propertyType if not already set to leasehold and state has changed
            if (currentPropertyTypeSelling !== leaseholdValue && prevPropertyTypeSellingRef.current !== leaseholdValue) {
                setStep1InitialValues((prev)=>{
                    if (!prev) return {
                        ...allCurrentValues,
                        propertyTypeSelling: leaseholdValue
                    };
                    return {
                        ...prev,
                        ...allCurrentValues,
                        propertyTypeSelling: leaseholdValue
                    };
                });
                prevPropertyTypeSellingRef.current = leaseholdValue;
            }
        } else {
            // Reset if "Flat" is deselected and currently set to leasehold
            // BUT only if "Flat" was previously selected (prevHasFlatSellingRef.current === true)
            // This means leasehold was set programmatically, so we should reset it
            if (currentPropertyTypeSelling === leaseholdValue && prevHasFlatSellingRef.current === true && // Was previously selected
            hasFlatSelling === false // Now deselected
            ) {
                setStep1InitialValues((prev)=>{
                    if (!prev) return prev;
                    const updated = {
                        ...prev,
                        ...allCurrentValues
                    };
                    updated.propertyTypeSelling = '';
                    return updated;
                });
                prevPropertyTypeSellingRef.current = '';
                prevHasFlatSellingRef.current = hasFlatSelling;
            } else if (prevHasFlatSellingRef.current !== hasFlatSelling) {
                // Update ref to track state change even if we don't reset
                prevHasFlatSellingRef.current = hasFlatSelling;
            }
        }
    }, [
        realTimeFormValues,
        step1FormData,
        filteredStep1FormFieldsWithPropertyType
    ]);
    // Dynamically determine which steps have forms
    // Map each step index to its form fields (if any)
    const stepFormFields = useMemo(()=>[
            filteredStep1FormFieldsWithPropertyType,
            undefined,
            step3FormFields
        ], [
        filteredStep1FormFieldsWithPropertyType,
        step3FormFields
    ]);
    const stepsWithForms = useMemo(()=>{
        // Check each step to see if it has form fields
        return stepFormFields.map((formFields, index)=>{
            if (!formFields) return false;
            // Step 0 (step1FormFields) is an array of objects with formFields property
            if (index === 0 && Array.isArray(formFields)) {
                return formFields.some((field)=>field?.formFields && Array.isArray(field.formFields) && field.formFields.length > 0);
            }
            // Step 2 (step3FormFields) is an object with formFields property
            if (index === 2 && typeof formFields === 'object' && 'formFields' in formFields) {
                return !!(formFields.formFields && Array.isArray(formFields.formFields) && formFields.formFields.length > 0);
            }
            return false;
        });
    }, [
        stepFormFields
    ]);
    // Handle step changes to reload form values from sessionStorage
    const handleActiveIndexChange = useCallback((newIndex)=>{
        // Reload form values from sessionStorage when navigating to a step with a form
        if (newIndex === 0) {
            const storedValues = getFormDataFromStorage(0);
            setStep1InitialValues(storedValues);
            setStep1FormData(storedValues);
        } else if (newIndex === 1) {
            // When navigating to step 2, reload step 1 form data to recalculate fees
            const storedValues = getFormDataFromStorage(0);
            setStep1FormData(storedValues);
        } else if (newIndex === 2) {
            const storedValues = getFormDataFromStorage(2);
            setStep3InitialValues(storedValues);
        }
    }, [
        getFormDataFromStorage
    ]);
    const handleBackClick = useCallback((args)=>{
        const { index, go } = args;
        // Clear session storage if going back from step 0 (whether it's a link or button)
        if (index === 0) {
            clearAllSessionStorage();
            // If it's a link, the navigation will happen via the link itself
            // If it's a button, we need to prevent default navigation since we're clearing storage
            // For links, we don't call go.prev() as the link will handle navigation
            // For buttons, we still need to navigate back
            return;
        }
        if (index === 2 && step3Ref.current) {
            // Step 3 form back navigation - persist current values without validation
            const formData = step3Ref.current.getFormValues();
            if (formData) {
                saveFormDataToStorage(2, formData);
            }
            go.prev();
            return;
        }
        go.prev();
    }, [
        clearAllSessionStorage,
        saveFormDataToStorage
    ]);
    const handleNextClick = useCallback(async (args)=>{
        const { index, go } = args;
        const stepTitle = (defaultSteps[index]?.title?.value ?? '').trim().split(' ').map((word)=>word.charAt(0).toUpperCase() + word.slice(1)).join('');
        const gtmEvent = toolSectionCompleteGTMEvent({
            toolName: getToolName(),
            sectionName: `${index + 1}_${stepTitle}`
        });
        sendGTMEvent(gtmEvent);
        // Handle form submission for steps with forms
        if (index === 0 && step1Ref.current) {
            // Step 1 form submission
            const formData = await step1Ref.current.submitForm();
            if (formData) {
                saveFormDataToStorage(0, formData);
                setStep1FormData(formData); // Update state immediately to trigger recalculation
                go.next();
            }
        } else if (index === 1) {
            // Step 2: Save step 2 data to sessionStorage before proceeding
            extractAndSaveStep2Data();
            go.next();
        } else if (index === 2 && step3Ref.current) {
            // Step 3 form submission - last step
            const formData = await step3Ref.current.submitForm();
            if (formData) {
                saveFormDataToStorage(2, formData);
                const gtmEventSubmit = toolStartFinishGTMEvent({
                    toolName: 'conveyancing_calc',
                    event: 'tool_submit'
                });
                sendGTMEvent(gtmEventSubmit);
                // Process adminEmailBody tokens with the form data
                const processedAdminEmailBody = replaceAdminEmailBodyTokens(emailTemplate?.fields?.adminEmailBody?.value, formData);
                const data = {
                    ...getAllFormDataFromStorage(),
                    ...emailRelatedFields,
                    adminEmailBody: {
                        ...emailRelatedFields.adminEmailBody,
                        value: processedAdminEmailBody
                    }
                };
                if (stepErrors[index] === true) return;
                if (!executeRecaptcha) {
                    console.error('reCAPTCHA is not available (missing site key or provider)');
                    setSubmitStatus('error');
                    return;
                }
                let recaptchaToken;
                try {
                    recaptchaToken = await executeRecaptcha(CONVEYANCING_CALCULATOR_CONSTANTS.RECAPTCHA_ACTION);
                } catch (error) {
                    console.error('Failed to generate reCAPTCHA token:', error);
                    setSubmitStatus('error');
                    return;
                }
                if (!recaptchaToken) {
                    setSubmitStatus('error');
                    return;
                }
                setIsLoading(true);
                const response = await apiRequest(CONVEYANCING_CALCULATOR_CONSTANTS.ENDPOINT_URL, {
                    method: 'POST',
                    data: await encryptData({
                        ...data,
                        recaptcha_token: recaptchaToken
                    }, process.env.ENCRYPTION_KEY || ''),
                    headers: {
                        sourceSystem: FORM_CONSTANTS.SOURCE_SYSTEM
                    }
                });
                if (response.success) {
                    // Process thankyouSuccessMessage with email address before clearing storage
                    setIsLoading(false);
                    const emailProcessedMessage = replaceEmailAddressToken(thankyouSuccessMessage?.value);
                    const processedMessage = {
                        ...thankyouSuccessMessage,
                        value: emailProcessedMessage
                    };
                    setProcessedThankYouMessage(processedMessage);
                    // Clear all session storage and show ThankYou component
                    clearAllSessionStorage();
                    setShowThankYou(true);
                } else {
                    setIsLoading(false);
                    console.error(response.error);
                    setSubmitStatus('error');
                }
            }
        } else {
            // Fallback: just proceed
            go.next();
        }
    }, [
        defaultSteps,
        getToolName,
        saveFormDataToStorage,
        extractAndSaveStep2Data,
        replaceAdminEmailBodyTokens,
        emailTemplate,
        getAllFormDataFromStorage,
        emailRelatedFields,
        replaceEmailAddressToken,
        thankyouSuccessMessage,
        clearAllSessionStorage,
        executeRecaptcha
    ]);
    // Callback to handle real-time form value changes
    const handleFormValuesChange = useCallback((values)=>{
        setRealTimeFormValues(values);
    }, []);
    const contents = [
        /*#__PURE__*/ _jsx(ConveyancingStep1, {
            ref: step1Ref,
            fields: filteredStep1FormFieldsWithPropertyType,
            guidanceBlock: step1FormGuidence,
            onValidationChange: handleStepValidationChange(0),
            onFormValuesChange: handleFormValuesChange,
            initialValues: step1InitialValues || undefined
        }, "step-1"),
        conveyancingStep2Content,
        /*#__PURE__*/ _jsx(ConveyancingStep3, {
            ref: step3Ref,
            fields: step3FormFields,
            guidanceBlock: step3FormGuidence,
            onValidationChange: handleStepValidationChange(2),
            initialValues: step3InitialValues || undefined
        }, "step-3")
    ];
    return /*#__PURE__*/ _jsx("div", {
        className: "py-component-section-container-padding-y",
        children: showThankYou ? /*#__PURE__*/ _jsx(ThankYou, {
            thankyouTitle: thankyouTitle,
            thankyouSuccessMessage: processedThankYouMessage || thankyouSuccessMessage,
            conveyancingCTALink: conveyancingCTALink,
            newjourneyCTALink: newjourneyCTALink
        }) : /*#__PURE__*/ _jsx(MultiStep, {
            steps: defaultSteps,
            storageKey: "conveyancing-steps",
            defaultActiveIndex: 0,
            resetKey: selectedOption && typeof selectedOption === 'string' ? selectedOption : 'buy',
            contents: contents,
            onActiveIndexChange: handleActiveIndexChange,
            ctaDescription: {
                label: ({ index })=>index === 0 ? step1FooterText : index === 1 ? step2FooterText : step3FooterText
            },
            showPrintButton: ({ index })=>index === 1 ? true : false,
            printDescription: printDescription,
            printLogoIcon: printLogoIcon,
            printTitle: printTitle,
            nav: {
                back: {
                    label: ({ index })=>index === 0 ? stepExitButtonText : stepBackButtonText,
                    onClick: handleBackClick
                },
                next: {
                    label: ({ index })=>{
                        if (isLoading && stepErrors[index] !== true) {
                            return {
                                value: 'Loading...'
                            };
                        }
                        return index === 0 ? step1NextButtonText : index === 1 ? step2NextButtonText : step3FinishButtonText;
                    },
                    disabled: ({ index })=>{
                        // Only disable if the step has a form and there are errors
                        const hasForm = stepsWithForms[index] ?? false;
                        // On last step (index 2), allow submission if no errors
                        // Otherwise, disable if there are errors
                        if (hasForm && index !== 2) {
                            // stepErrors[index] will be false when no errors, true when errors exist
                            // We want to disable only when stepErrors[index] === true
                            return stepErrors[index] === true;
                        }
                        return false;
                    },
                    onClick: handleNextClick
                }
            }
        })
    });
};
const ConveyancingCalculator = (props)=>{
    const { sitecoreContext } = useSitecoreContext();
    const reCAPTCHASiteKey = sitecoreContext?.SiteSettings?.reCAPTCHASiteKey || process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY || '';
    return /*#__PURE__*/ _jsx(GoogleReCaptchaProvider, {
        reCaptchaKey: reCAPTCHASiteKey,
        children: /*#__PURE__*/ _jsx(ConveyancingCalculatorInner, {
            ...props
        })
    });
};
export default ConveyancingCalculator;
